import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

export default function IntroVideo() {
  const vref = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = vref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = vref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      id="intro-video"
      data-testid="intro-video-section"
      className="relative bg-[#050505] px-6 md:px-12 py-24 md:py-28 border-t border-white/10"
    >
      <div className="mx-auto max-w-[1400px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]">Watch the Story</span>
          <h2 className="mt-4 font-display text-5xl md:text-7xl uppercase tracking-tight text-white">
            Meet Delta DL Enterprise, LLC
          </h2>
          <p className="mt-6 max-w-2xl text-lg text-white/60">
            A cinematic look at the classroom, the craft, and the community behind every book,
            program, and artwork.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-10 relative overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_20px_80px_-20px_rgba(124,58,237,0.35)]"
        >
          <video
            ref={vref}
            data-testid="intro-video"
            className="w-full aspect-video object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/hero.mp4"
          >
            <source src="/intro.mp4" type="video/mp4" />
            <source src="/hero.mp4" type="video/mp4" />
          </video>

          <div className="absolute bottom-4 left-4 flex gap-2">
            <button
              data-testid="intro-play-btn"
              onClick={togglePlay}
              aria-label={playing ? "Pause video" : "Play video"}
              className="rounded-full bg-black/70 backdrop-blur px-3 py-2 text-white hover:bg-[#7C3AED] transition-colors"
            >
              {playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <button
              data-testid="intro-mute-btn"
              onClick={toggleMute}
              aria-label={muted ? "Unmute video" : "Mute video"}
              className="rounded-full bg-black/70 backdrop-blur px-3 py-2 text-white hover:bg-[#7C3AED] transition-colors"
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            {muted && (
              <span
                data-testid="intro-sound-hint"
                className="pointer-events-none self-center animate-pulse rounded-full bg-[#7C3AED]/90 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-white"
              >
                Tap for sound
              </span>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
